import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";

import {
  Box, Button, Card, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, MenuItem, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, alpha,
} from "@mui/material";
import { Add, Delete, Edit, SupervisorAccount } from "@mui/icons-material";

import MainContainer from "../../components/MainContainer";
import ForbiddenPage from "../../components/ForbiddenPage";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const emptyForm = { name: "", email: "", password: "", status: "active", companyIds: [] };

const Supervisors = () => {
  const { user } = useContext(AuthContext);

  const [supervisors, setSupervisors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/supervisors"),
        api.get("/companies/list"),
      ]);
      setSupervisors(s.data || []);
      setCompanies((c.data && (c.data.companies || c.data)) || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user.super) load();
    // eslint-disable-next-line
  }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };

  const openEdit = (sup) => {
    setEditing(sup);
    setForm({
      name: sup.name, email: sup.email, password: "", status: sup.status,
      companyIds: (sup.companies || []).map((x) => x.companyId),
    });
    setOpen(true);
  };

  const toggleCompany = (id) => {
    setForm((prev) => ({
      ...prev,
      companyIds: prev.companyIds.includes(id)
        ? prev.companyIds.filter((c) => c !== id)
        : [...prev.companyIds, id],
    }));
  };

  const handleSave = async () => {
    if (!form.name || !form.email) return toast.warn("Nome e email são obrigatórios.");
    if (!editing && !form.password) return toast.warn("Defina uma senha.");
    if (form.companyIds.length === 0) return toast.warn("Selecione ao menos uma empresa.");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/supervisors/${editing.id}`, form);
        toast.success("Supervisor atualizado!");
      } else {
        await api.post("/supervisors", form);
        toast.success("Supervisor criado!");
      }
      setOpen(false);
      load();
    } catch (err) { toastError(err); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover este supervisor? Os acessos dele nas empresas serão apagados.")) return;
    try { await api.delete(`/supervisors/${id}`); toast.success("Supervisor removido."); load(); }
    catch (err) { toastError(err); }
  };

  if (!user.super) return <ForbiddenPage />;

  return (
    <MainContainer>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2.5, background: alpha("#00838f", 0.15), display: "flex", alignItems: "center", justifyContent: "center" }}>
              <SupervisorAccount sx={{ color: "#00838f" }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Supervisores</Typography>
              <Typography variant="body2" color="text.secondary">Usuários que acessam e auditam várias empresas</Typography>
            </Box>
          </Stack>
          <Button variant="contained" startIcon={<Add />} onClick={openNew} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
            Novo Supervisor
          </Button>
        </Stack>

        <Card elevation={0} sx={{ borderRadius: 3, border: "1.5px solid", borderColor: "divider", overflow: "hidden" }}>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Empresas</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {supervisors.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>{s.email}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
                        {(s.companies || []).map((c) => (
                          <Chip key={c.companyId} size="small" label={c.companyName} sx={{ background: alpha("#00838f", 0.12), color: "#00838f", fontWeight: 600 }} />
                        ))}
                        {(s.companies || []).length === 0 && <Typography variant="caption" color="text.secondary">nenhuma</Typography>}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={s.status === "active" ? "Ativo" : "Inativo"} sx={{ background: s.status === "active" ? alpha("#2e7d32", 0.15) : alpha("#757575", 0.15), color: s.status === "active" ? "#2e7d32" : "#757575", fontWeight: 600 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(s)}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Remover"><IconButton size="small" onClick={() => handleDelete(s.id)}><Delete fontSize="small" color="error" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && supervisors.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>Nenhum supervisor cadastrado.</TableCell></TableRow>}
                {loading && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </Box>
        </Card>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{editing ? "Editar Supervisor" : "Novo Supervisor"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}><TextField label="Nome" size="small" fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Email" size="small" fullWidth value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField label={editing ? "Nova senha (vazio = manter)" : "Senha"} type="password" size="small" fullWidth value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Status" size="small" fullWidth value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="active">Ativo</MenuItem>
                <MenuItem value="inactive">Inativo</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Empresas que ele pode acessar</Typography>
              <Box sx={{ maxHeight: 220, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1 }}>
                {companies.map((c) => (
                  <FormControlLabel key={c.id}
                    control={<Checkbox size="small" checked={form.companyIds.includes(c.id)} onChange={() => toggleCompany(c.id)} />}
                    label={<Typography variant="body2">{c.name}</Typography>}
                    sx={{ display: "flex", ml: 0 }}
                  />
                ))}
                {companies.length === 0 && <Typography variant="caption" color="text.secondary">Nenhuma empresa encontrada.</Typography>}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
            {saving ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Supervisors;
